import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { itineraryApi } from '../../api/itinerary.api';
import type { NextDayUpdate } from '../../api/itinerary.api';
import { venuesApi } from '../../api/venues.api';
import { tripsApi } from '../../api/trips.api';
import { buildGoogleMapsUrl } from '../../utils/googleMaps';
import type { Activity, ActivityType, Flight, Hotel as HotelType, ItineraryDay, Trip, TripPhoto, Venue } from '../../types';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import LoadingScreen from '../../components/ui/LoadingScreen';
import Spinner from '../../components/ui/Spinner';
import { toast } from '../../components/ui/Toast';
import { format, parseISO } from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';
import { useTripWeather } from '../../hooks/useTripWeather';
import { tripWeatherKey } from '../../utils/tripWeatherKey';
import { dayPlaceLabel } from '../../utils/tripLegs';
import { DayWeatherChip } from '../../components/trips/itinerary/DayWeatherChip';
import { HourlyWeatherPanel } from '../../components/trips/itinerary/HourlyWeatherPanel';
import type { DayWeather } from '../../api/weather.api';
import { fmtUTCTime, initialItineraryDay, localDateKey } from '../../utils/dateTime';
import { applyDaySchedule } from '../../utils/activityOrder';
import { deleteEntry, reorderEntry, stopIds } from '../../utils/itineraryHistoryEntries';
import { removeActivitiesLocally } from '../../utils/removeActivities';
import {
  DndContext, closestCenter,
  useSensor, useSensors, MouseSensor, TouchSensor,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ActivityForm from '../../components/itinerary/ActivityForm';
import { useAiPanel, useAiPanelState } from '../../components/ai/AiPanelContext';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, List, GripVertical, AlignLeft,
  Utensils, Coffee, Landmark, Eye, Building2,
  TreePine, ShoppingBag, Sparkles, Bus, Hotel, MapPin, SquareParking, Waves, Mountain,
  Clock, Trash2, CircleDashed, CheckCircle2, Icon,
} from '../../components/ui/Icon';
import { DayStrip } from '../../components/trips/itinerary/DayStrip';
import { DayStartTime } from '../../components/trips/itinerary/DayStartTime';
import { TimelineRail } from '../../components/trips/itinerary/TimelineRail';
import { DayPickerMenu } from '../../components/trips/itinerary/DayPickerMenu';
import { UndoRedoControls } from '../../components/trips/itinerary/UndoRedoControls';
import { useCrossDayMove } from '../../hooks/useCrossDayMove';
import { useDayLegs } from '../../hooks/useDayLegs';
import { useDayOptimizer } from '../../hooks/useDayOptimizer';
import { useStopPhotos } from '../../hooks/useStopPhotos';
import { StopPhotoViewer } from '../../components/trips/itinerary/StopPhotoViewer';
import { tripPermissions } from '../../utils/tripPermissions';
import AiRouteProposalModal from '../../components/map/AiRouteProposalModal';
import LinkedDeleteModal from '../../components/trips/LinkedDeleteModal';
import EmptyState from '../../components/ui/EmptyState';

// ── Constants ──────────────────────────────────────────────────────────────────
const TYPE_ICON: Record<ActivityType, React.ElementType> = {
  RESTAURANT: Utensils, CAFE: Coffee, MONUMENT: Landmark, VIEWPOINT: Eye,
  NEIGHBORHOOD: Building2, PARK: TreePine, SHOP: ShoppingBag,
  EXPERIENCE: Sparkles, TRANSPORT: Bus, PARKING: SquareParking, BEACH: Waves, NATURE: Mountain, ACCOMMODATION: Hotel, OTHER: MapPin,
};

const TYPE_COLOR: Record<ActivityType, string> = {
  RESTAURANT: 'text-orange-500', CAFE: 'text-amber-600', MONUMENT: 'text-violet-500',
  VIEWPOINT: 'text-sky-500', NEIGHBORHOOD: 'text-indigo-500', PARK: 'text-green-500',
  SHOP: 'text-pink-500', EXPERIENCE: 'text-teal-500', TRANSPORT: 'text-slate-500',
  PARKING: 'text-blue-600', BEACH: 'text-cyan-500', NATURE: 'text-lime-600', ACCOMMODATION: 'text-blue-500', OTHER: 'text-slate-400',
};

// ── Sortable timeline activity ────────────────────────────────────────────────
function SortableTimelineActivity({ activity, onEdit, onDelete, visited, onToggleVisited }: {
  activity: Activity;
  onEdit: (a: Activity) => void;
  onDelete: (a: Activity) => void;
  /** Same contract as ActivityCard: no handler, no button. */
  visited?: boolean;
  onToggleVisited?: () => void;
}) {
  const { t } = useTranslation();
  // isOrigin mirrors the previous day's last stop — not a real independent activity,
  // so it can't be dragged or deleted here either (same protection as List view).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: activity.id, disabled: activity.isOrigin });
  const ActivityTypeIcon = TYPE_ICON[activity.type] ?? MapPin;
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className={`flex items-start rounded-xl border shadow-sm overflow-hidden ${
        activity.isOptional && !activity.isOrigin ? 'border-dashed ' : ''
      }${
        activity.isOrigin
          ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700'
          : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-700'
      }`}
    >
      {activity.isOrigin ? (
        <div className="self-stretch px-2 shrink-0 flex items-center border-r border-teal-200 dark:border-teal-700 text-teal-400">
          <Icon as={MapPin} size={14} />
        </div>
      ) : (
        <button
          {...attributes} {...listeners}
          className="self-stretch px-2 text-slate-200 hover:text-slate-400 cursor-grab active:cursor-grabbing shrink-0 flex items-center border-r border-slate-100 dark:border-slate-700"
          aria-label={t('common.dragToReorder')}
          onClick={e => e.stopPropagation()}
        >
          <Icon as={GripVertical} size={14} />
        </button>
      )}

      <button type="button" onClick={() => onEdit(activity)} className="flex-1 flex items-start gap-3 px-4 py-3 text-left cursor-pointer min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-slate-50 dark:bg-slate-800">
          <Icon as={ActivityTypeIcon} size={16} className={TYPE_COLOR[activity.type] ?? 'text-slate-400'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{activity.title}</p>
            {activity.customType && !activity.isOrigin && (
              <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 shrink-0">
                {activity.customType}
              </span>
            )}
            {activity.isOrigin && (
              <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 shrink-0">
                {t('itinerary.startFromPrev.active')}
              </span>
            )}
            {activity.isOptional && !activity.isOrigin && (
              <span
                title={t('itinerary.optional.hint')}
                className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 shrink-0"
              >
                <Icon as={CircleDashed} size={9} />
                {t('itinerary.optional.badge')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {activity.venueName && <span className="text-2xs text-slate-400 truncate">{activity.venueName}</span>}
            {activity.zone && <span className="text-2xs text-slate-400">{activity.zone}</span>}
          </div>
        </div>
        {activity.startTime && (
          <div className="flex items-center gap-1 shrink-0">
            <Icon as={Clock} size={12} className="text-slate-300" />
            <span className="text-2xs text-slate-400">{activity.startTime}</span>
          </div>
        )}
      </button>

      {!activity.isOrigin && onToggleVisited && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onToggleVisited(); }}
          className={`self-stretch px-2.5 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center ${
            visited
              ? 'text-green-500 hover:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              : 'text-slate-300 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
          }`}
          aria-label={visited ? t('venues.markUnvisited') : t('venues.markVisited')}
          title={visited ? t('venues.markUnvisited') : t('venues.markVisited')}
          aria-pressed={!!visited}
        >
          <Icon as={CheckCircle2} size={14} />
        </button>
      )}

      {!activity.isOrigin && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
          className="self-stretch px-2.5 text-slate-300 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0 border-l border-slate-100 dark:border-slate-700 flex items-center"
          aria-label={t('itinerary.delete.ariaLabel')}
        >
          <Icon as={Trash2} size={14} />
        </button>
      )}

      <ConfirmModal
        open={confirmDelete}
        title={t('itinerary.delete.title')}
        body={t('itinerary.delete.body')}
        onConfirm={() => { onDelete(activity); setConfirmDelete(false); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

/** The day's last real stop — the one the next day's origin card mirrors. */
function lastStopOf(day: ItineraryDay | undefined | null): Activity | null {
  if (!day) return null;
  return day.activities.filter(a => !a.isOrigin).sort((a, b) => a.sortOrder - b.sortOrder).at(-1) ?? null;
}

// ── Timeline view ──────────────────────────────────────────────────────────────
type TimelineEvent =
  | { kind: 'flight'; data: Flight; date: string }
  | { kind: 'activity'; data: Activity; date: string; dayTitle: string | null; dayNumber: number };

function TimelineView({ days, trip, weather, onOpenHourly, onReorderActivities, onMoveToDay, onEditActivity, onDeleteActivity, visitedOf, onToggleVisited }: {
  days: ItineraryDay[];
  trip: Trip;
  weather: Record<string, DayWeather>;
  onOpenHourly: (date: string) => void;
  onReorderActivities: (dayId: string, orderedIds: string[]) => Promise<void>;
  onMoveToDay: (activityIds: string[], targetDayId: string, atIndex: number) => void;
  onEditActivity: (a: Activity) => void;
  onDeleteActivity: (a: Activity) => void;
  /** Both views of the itinerary offer the same toggle — see venueForActivity.
      The origin card never shows it: it mirrors yesterday's last stop. */
  visitedOf: (a: Activity) => boolean | undefined;
  onToggleVisited: (a: Activity) => void;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  // Touch: press-and-hold (200ms) to drag so a finger swipe scrolls the list instead of being
  // eaten by an accidental reorder; mouse keeps a small distance threshold. See TimelineRail for
  // the same rationale.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  // Local per-day activity order (keyed by dayId) for drag state
  const [localActivities, setLocalActivities] = useState<Record<string, Activity[]>>(
    () => Object.fromEntries(days.map(d => [d.id, [...d.activities].sort((a, b) => a.sortOrder - b.sortOrder)])),
  );

  useEffect(() => {
    setLocalActivities(Object.fromEntries(days.map(d => [d.id, [...d.activities].sort((a, b) => a.sortOrder - b.sortOrder)])));
  }, [days]);

  // The same promise the list view makes with its day strip: during the trip
  // you land on today, not at the top of a scroll. Once per mount — re-running
  // it would yank the page back while the user is reading another day.
  const todayKey = localDateKey();
  const todayRef = useRef<HTMLDivElement>(null);
  const scrolledToToday = useRef(false);
  useEffect(() => {
    if (scrolledToToday.current || !todayRef.current) return;
    scrolledToToday.current = true;
    todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [days.length]);

  // Build flat event list using local activity order
  const events: TimelineEvent[] = [];
  for (const day of days) {
    // Slice to date-only so this matches the flight/hotel date keys below (day.date
    // is a full ISO datetime like "2026-09-11T00:00:00.000Z") — otherwise a day with
    // both a flight and activities splits into two separate date groups.
    const dateKey = day.date.slice(0, 10);
    for (const act of (localActivities[day.id] ?? day.activities)) {
      events.push({ kind: 'activity', data: act, date: dateKey, dayTitle: day.title, dayNumber: day.dayNumber });
    }
  }
  for (const flight of trip.flights ?? []) {
    events.push({ kind: 'flight', data: flight, date: flight.departureTime.split('T')[0] });
  }


  events.sort((a, b) => a.date.localeCompare(b.date));

  const byDate: Record<string, TimelineEvent[]> = {};
  for (const ev of events) {
    (byDate[ev.date] ??= []).push(ev);
  }

  /** Which day a card is currently sitting in — the timeline shows them all. */
  function dayIdOf(activityId: string): string | null {
    for (const [dayId, acts] of Object.entries(localActivities)) {
      if (acts.some(a => a.id === activityId)) return dayId;
    }
    return null;
  }

  /**
   * One context spans every day here, so a drop is only a reorder when it lands
   * back in the day it came from; otherwise it is a move, and the card holds the
   * slot it was dropped into rather than being appended.
   */
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceDayId = dayIdOf(active.id as string);
    const targetDayId = dayIdOf(over.id as string);
    if (!sourceDayId || !targetDayId) return;

    if (sourceDayId === targetDayId) { reorderWithinDay(sourceDayId, event); return; }

    const stops = (localActivities[targetDayId] ?? []).filter(a => !a.isOrigin);
    // A drop onto the target's origin card means "first" — it is pinned ahead of
    // every real stop, so index 0 is the nearest slot the card may take.
    const atIndex = Math.max(0, stops.findIndex(a => a.id === over.id));
    onMoveToDay([active.id as string], targetDayId, atIndex);
  }

  function reorderWithinDay(dayId: string, event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const acts = localActivities[dayId] ?? [];
    const oldIdx = acts.findIndex(a => a.id === active.id);
    let newIdx = acts.findIndex(a => a.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;

    // The origin card (continues from the previous day) can't be dragged itself
    // (see `disabled` on its useSortable), but other cards could still be dropped
    // ahead of it — clamp so it always stays first.
    const firstNonOriginIdx = acts.findIndex(a => !a.isOrigin);
    if (firstNonOriginIdx > 0) newIdx = Math.max(newIdx, firstNonOriginIdx);
    if (newIdx === oldIdx) return;

    // Position is the source of truth: re-deal the day's times to the new order so
    // the list stays chronological (mirrors the server) instead of showing the
    // dragged card with its old, now out-of-place time.
    const reordered = applyDaySchedule(
      days.find(d => d.id === dayId)?.startTime ?? null,
      arrayMove(acts, oldIdx, newIdx),
    );

    // Update local state immediately (outside setState callback — no async side-effects inside)
    setLocalActivities(prev => ({ ...prev, [dayId]: reordered }));

    // Persist to API after state update
    onReorderActivities(dayId, reordered.map(a => a.id));
  }

  return (
    // A single context across every day: a per-day one made the cards on screen
    // unreachable from each other, which is the one thing this view is for.
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
    <div className="relative pl-6 space-y-0 animate-fade-in">
      {/* Vertical line */}
      <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-100 dark:bg-slate-700 rounded-full" />

      {Object.entries(byDate).map(([date, evs]) => {
        const day = days.find(d => d.date === date || d.date.startsWith(date));
        const activities = evs.filter(e => e.kind === 'activity') as (TimelineEvent & { kind: 'activity' })[];
        const others = evs.filter(e => e.kind !== 'activity');
        const place = day ? dayPlaceLabel(trip.destinations ?? [], day.dayNumber) : null;

        return (
          <div key={date} ref={date === todayKey ? todayRef : undefined} className="relative mb-10">
            {/* Date dot */}
            <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-teal-400 border-2 border-white dark:border-slate-950 ring-1 ring-teal-200 z-10" />

            {/* Date label */}
            <div className="mb-3 pt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-display text-sm font-semibold text-slate-800 dark:text-slate-200">
                {format(parseISO(date), 'EEE, d MMM', { locale: dateLocale })}
              </span>
              {day && (
                <span className="text-xs text-slate-400">
                  {t('itinerary.dayLabel', { n: day.dayNumber })}{day.title ? ` · ${day.title}` : ''}
                  {place ? ` · ${place}` : ''}
                </span>
              )}
              {weather[date] && (
                <DayWeatherChip
                  weather={weather[date]}
                  variant="full"
                  locale={dateLocale}
                  onClick={() => onOpenHourly(date)}
                />
              )}
            </div>

            <div className="space-y-2">
              {(() => {
                // The origin card (continues from the previous day) always renders
                // first — even before today's flights/hotels — so it reads as "this
                // is where the day starts", matching every other view.
                const originEv = activities.find(ev => ev.data.isOrigin);
                const restEvs = activities.filter(ev => !ev.data.isOrigin);

                const flightRows = (
                  <>
                    {/* Flights */}
                    {others.filter(e => e.kind === 'flight').map((ev, i) => {
                      const f = (ev as TimelineEvent & { kind: 'flight' }).data;
                      return (
                        <div key={`flight-${f.id}-${i}`} className="flex items-center gap-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800 px-4 py-3">
                          <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center shrink-0">
                            <Icon as={Bus} size={16} className="text-sky-600 dark:text-sky-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-sky-900 dark:text-sky-300">
                              {f.direction === 'OUTBOUND' ? t('itinerary.flight.outbound') : t('itinerary.flight.return')} · {f.airline ? `${f.airline}` : ''}
                              {f.flightNumber ? ` ${f.flightNumber}` : ''}
                            </p>
                            <p className="text-xs text-sky-600 dark:text-sky-400 mt-0.5">
                              {f.departureAirport} → {f.arrivalAirport}
                              {f.departureTime ? ` · ${fmtUTCTime(f.departureTime)}` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                  </>
                );

                if (activities.length === 0 || !day) return flightRows;

                return (
                  <SortableContext items={activities.map(ev => ev.data.id)} strategy={verticalListSortingStrategy}>
                      {originEv && (
                        <SortableTimelineActivity key={originEv.data.id} activity={originEv.data} onEdit={onEditActivity} onDelete={onDeleteActivity} />
                      )}

                      {flightRows}

                      {restEvs.length > 0 && (
                        <div className="space-y-2">
                          {restEvs.map(ev => (
                            <SortableTimelineActivity
                              key={ev.data.id}
                              activity={ev.data}
                              onEdit={onEditActivity}
                              onDelete={onDeleteActivity}
                              visited={visitedOf(ev.data)}
                              onToggleVisited={visitedOf(ev.data) === undefined ? undefined : () => onToggleVisited(ev.data)}
                            />
                          ))}
                        </div>
                      )}
                  </SortableContext>
                );
              })()}
            </div>
          </div>
        );
      })}

      {events.length === 0 && (
        <EmptyState size="inline" icon={CalendarDays} title={t('itinerary.empty.noEvents')} />
      )}
    </div>
    </DndContext>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ItineraryTab() {
  const { trip, days, setDays, history } = useTripContext();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  // Fetched once here and handed to both views, so switching list ↔ timeline
  // doesn't re-request it.
  const weatherKey = tripWeatherKey(trip);
  const placeOf = (dayNumber: number) => dayPlaceLabel(trip.destinations ?? [], dayNumber);
  const { byDate: weather, loading: weatherLoading } = useTripWeather(trip.id, weatherKey);
  const [loading, setLoading]         = useState(true);
  const [viewMode, setViewMode]       = useState<'list' | 'timeline'>('list');
  // Itinerary AI proposals are applied centrally via TripContext (see
  // useAiActionDispatch), so no per-tab handler is registered here — only the
  // routing-hint context. This makes them apply even from other tabs.
  useAiPanel({ context: 'itinerary' });
  const { askAi } = useAiPanelState();
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState<ItineraryDay | null>(null);
  const [editActivity, setEditActivity]   = useState<Activity | null>(null);
  const [addingTo, setAddingTo]           = useState<{ dayId: string } | null>(null);
  const [venues, setVenues]               = useState<Venue[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMenu, setBulkMenu] = useState<'move' | 'duplicate' | null>(null);
  // A delete that includes stops linked to meals, waiting for "and the meals too?" (ADR-0024).
  const [linkedDelete, setLinkedDelete] = useState<{ ids: string[]; meals: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const stopPhotos = useStopPhotos(trip);
  const [openPhotos, setOpenPhotos] = useState<{ photos: TripPhoto[]; index: number } | null>(null);
  // Shared with the timeline view and the map's routes panel — the same move,
  // however the gesture reaches it.
  const crossDay = useCrossDayMove({ tripId: trip.id, days, setDays: history.setDays, history });
  // Travel times: free on mount (cache only), and the open day's missing legs
  // bought for it — one day, never the trip (ADR-0011).
  const refetchDayAfterLegs = useCallback(async (dayId: string) => {
    // The server rebuilt this day's clock with the travel it just resolved.
    const fresh = await itineraryApi.listDays(trip.id);
    setDays(fresh);
    setSelectedDay(s => (s?.id === dayId ? fresh.find(d => d.id === dayId) ?? s : s));
  }, [trip.id, setDays]);
  const legs = useDayLegs(trip.id, days, selectedDay?.id ?? null, refetchDayAfterLegs);
  // The same optimiser the map's routes panel uses, not a second one.
  const optimizer = useDayOptimizer({ tripId: trip.id, days, setDays: history.setDays, history });
  const [togglingStartFromPrev, setTogglingStartFromPrev] = useState(false);
  // The day whose trace the panel opens on; `null` means the panel is closed.
  const [hourlyDate, setHourlyDate] = useState<string | null>(null);

  // A saved place must open in Google Maps exactly as it does in the Places tab:
  // reuse the linked venue's canonical mapsUrl (falling back to the same
  // name-based search Places uses). Free-text activities resolve from their own
  // fields.
  /**
   * The saved place behind a stop. Two callers now — the Maps link and the
   * visited toggle — and they must agree: a stop that opens a venue's Maps URL
   * is the same stop whose venue gets marked visited.
   */
  const venueForActivity = useCallback(
    (a: Activity): Venue | undefined => (a.venueId ? venues.find(v => v.id === a.venueId) : undefined),
    [venues],
  );

  /**
   * ADR-0004 note for the port: `visited` lives on `Venue`, not on `Activity`
   * (schema.prisma:463). So the same place marked on day 2 shows marked on day
   * 5 — it is one place — and a stop with no saved place has nothing to write
   * to and gets no toggle at all.
   */
  const toggleVisited = useCallback(async (a: Activity) => {
    const venue = venueForActivity(a);
    if (!venue) return;
    const previous = venue.visited;
    // Optimistic: the toggle is a glance, not a transaction.
    setVenues(prev => prev.map(v => (v.id === venue.id ? { ...v, visited: !previous } : v)));
    try {
      const updated = await venuesApi.toggleVisited(trip.id, venue.id, !previous);
      setVenues(prev => prev.map(v => (v.id === venue.id ? updated : v)));
    } catch {
      setVenues(prev => prev.map(v => (v.id === venue.id ? { ...v, visited: previous } : v)));
      toast(t('venues.toast.saveFailed'), 'error');
    }
  }, [venueForActivity, trip.id, t]);

  const resolveActivityMapsUrl = useCallback((a: Activity): string | null => {
    const venue = venueForActivity(a);
    if (venue) {
      return venue.mapsUrl ?? buildGoogleMapsUrl({
        name: venue.name,
        placeId: venue.placeId,
        latitude: venue.latitude,
        longitude: venue.longitude,
        address: venue.address,
        zone: venue.zone,
        destination: trip.destination,
      });
    }
    return buildGoogleMapsUrl({
      name: a.venueName || a.title,
      placeId: a.placeId,
      latitude: a.latitude,
      longitude: a.longitude,
      address: a.address,
      zone: a.zone,
      destination: trip.destination,
    });
  }, [venueForActivity, trip.destination]);

  useEffect(() => {
    async function init() {
      try {
        // If days are already in context (loaded by TripDetailPage or synced from
        // MapTab's reorder), skip re-fetching to avoid overwriting in-memory changes.
        if (days.length > 0) {
          const v = await venuesApi.list(trip.id);
          setVenues(v);
          if (!selectedDay) setSelectedDay(initialItineraryDay(days));
          return;
        }
        const [d_raw, v] = await Promise.all([
          itineraryApi.listDays(trip.id),
          venuesApi.list(trip.id),
        ]);
        let d = d_raw;
        setVenues(v);
        if (d.length === 0 && trip.startDate && trip.endDate) {
          // A trip from before days were created at creation time. Asking the
          // server to re-sync them is one request instead of one POST per day
          // (a 14-day trip used to be 14 sequential round trips on first open),
          // and it is the same code path that keeps them in step when the dates
          // change — so there is only one definition of "the days of a trip".
          await tripsApi.update(trip.id, {
            startDate: trip.startDate.slice(0, 10),
            endDate:   trip.endDate.slice(0, 10),
          });
          d = await itineraryApi.listDays(trip.id);
        }
        setDays(d);
        if (d.length > 0) setSelectedDay(initialItineraryDay(d));
      } catch {
        toast(t('itinerary.toast.loadFailed'), 'error');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [trip.id]);

  // Keep selectedDay in sync when shared days change (e.g. updated from MapTab)
  useEffect(() => {
    if (selectedDay) {
      const fresh = days.find(d => d.id === selectedDay.id);
      if (fresh) setSelectedDay(fresh);
    }
  }, [days]);

  // ADR-0010 + ADR-0011: if the day already starts with something at 05:50, the
  // day starts at 05:50. Initialised once, from the first stop that carries an
  // hour, and never invented — a day whose stops are all untimed keeps its
  // button, because picking a number for it would cascade guesses over every
  // stop. It stays a stored field afterwards, which is what makes it survive a
  // reorder; deriving it from the first stop on every render is the bug
  // ADR-0010 named.
  const initialisedDays = useRef<Set<string>>(new Set());
  useEffect(() => {
    const day = selectedDay;
    if (!day || day.startTime || initialisedDays.current.has(day.id)) return;
    const firstHour = [...day.activities]
      .filter(a => !a.isOrigin && a.startTime)
      .sort((a, b) => a.sortOrder - b.sortOrder)[0]?.startTime;
    if (!firstHour) return;

    initialisedDays.current.add(day.id);
    itineraryApi.updateDay(trip.id, day.id, { startTime: firstHour })
      .then(updated => setDays(d => d.map(dy => dy.id === day.id ? { ...dy, ...updated } : dy)))
      .catch(() => { /* the button is still there; nothing is lost */ });
  }, [selectedDay, trip.id, setDays]);

  // Clear selection when switching days
  useEffect(() => { setSelectedIds(new Set()); }, [selectedDay?.id]);

  // Offering "move to" the day the cards already sit on is a no-op dressed as
  // an action; when the selection spans days there is no such day to grey out.
  const soleSelectedDayId = useMemo(() => {
    const dayIds = new Set(
      days.filter(d => d.activities.some(a => selectedIds.has(a.id))).map(d => d.id),
    );
    return dayIds.size === 1 ? [...dayIds][0] : null;
  }, [days, selectedIds]);

  function toggleSelectId(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  /**
   * ADR-0015: the cards leave the plan now and the DELETE goes out when the undo
   * window closes, so undoing is not sending it. Single and bulk delete alike.
   */
  async function deleteActivities(ids: string[], withMeals?: boolean) {
    const deleting = days.flatMap(d => d.activities).filter(a => ids.includes(a.id));
    if (deleting.length === 0) return;
    // ADR-0024: asked now, at the gesture; the answer travels with the deferred
    // DELETE, so undoing still sends nothing for either.
    const linkedMeals = deleting.filter(a => a.mealId).length;
    if (linkedMeals > 0 && withMeals === undefined) {
      setLinkedDelete({ ids, meals: linkedMeals });
      return;
    }
    const before = days;
    await history.run(async () => {
      history.setDays(prev => removeActivitiesLocally(prev, ids));
      return deleteEntry(
        { tripId: trip.id, setDays: history.setDays, getDays: history.getDays },
        { activities: deleting, before, withMeals: withMeals ?? false, message: t('itinerary.toast.deleted') },
      );
    });
  }

  async function bulkDeleteActivities() {
    const ids = [...selectedIds];
    setSelectedIds(new Set());
    await deleteActivities(ids);
  }

  async function reorderDay(dayId: string, orderedIds: string[]) {
    const before = stopIds(days.find(d => d.id === dayId));
    await history.run(async () => {
      const { nextDayUpdate, activities } = await itineraryApi.reorderActivities(trip.id, dayId, orderedIds);
      history.setDays(d => {
        let updated = d.map(day => day.id === dayId ? { ...day, activities } : day);
        if (nextDayUpdate) updated = applyNextDayUpdate(updated, nextDayUpdate);
        return updated;
      });
      return reorderEntry(
        { tripId: trip.id, setDays: history.setDays, getDays: history.getDays },
        dayId, before, activities.filter(a => !a.isOrigin).map(a => a.id),
        t('itinerary.undo.reordered'),
      );
    });
  }

  function applyNextDayUpdate(dayList: ItineraryDay[], ndu: NextDayUpdate): ItineraryDay[] {
    return dayList.map(d => {
      if (d.id !== ndu.dayId) return d;
      return {
        ...d,
        startFromPrevStop: true,
        activities: [ndu.originActivity, ...d.activities.filter(a => !a.isOrigin)],
      };
    }) as ItineraryDay[];
  }

  // `dayActivities`, when present, is the day's non-origin list already re-sorted
  // chronologically by the server (a timed activity slotted before existing ones).
  // Use it verbatim so neighbours' new sortOrder is reflected; otherwise the plain
  // append is correct (the new activity carries its final sortOrder and nothing
  // else moved). The origin marker is kept pinned in front either way.
  function mergeDayActivities(existing: Activity[], activity: Activity, dayActivities: Activity[] | null): Activity[] {
    const origin = existing.filter(a => a.isOrigin);
    const nonOrigin = dayActivities ?? [...existing.filter(a => !a.isOrigin), activity];
    return [...origin, ...nonOrigin];
  }

  function handleActivityCreated(
    activity: Activity,
    nextDayUpdate: NextDayUpdate | null = null,
    dayActivities: Activity[] | null = null,
    newVenue: Venue | null = null,
  ) {
    // The stop pinned a place the trip hadn't saved: the server added it to
    // Places, so the picker and the maps links here must see it without a
    // refetch (the Places tab loads its own list when opened).
    if (newVenue) setVenues(prev => [...prev, newVenue]);
    setDays(prev => {
      let updated = prev.map(d =>
        d.id === activity.dayId ? { ...d, activities: mergeDayActivities(d.activities, activity, dayActivities) } : d,
      );
      if (nextDayUpdate) updated = applyNextDayUpdate(updated, nextDayUpdate);
      return updated;
    });
    if (selectedDay?.id === activity.dayId) {
      setSelectedDay(s => s ? { ...s, activities: mergeDayActivities(s.activities, activity, dayActivities) } : s);
    }
    setAddingTo(null);
  }

  // The origin card is a copy of the previous day's last stop, rebuilt on every
  // sync — editing the copy would be silently undone. Both cards are the same
  // stop to the user, so clicking either opens the one that actually stores it.
  function editActivityOrSource(activity: Activity) {
    if (!activity.isOrigin) return setEditActivity(activity);
    const day = days.find(d => d.id === activity.dayId);
    const source = day && lastStopOf(days.find(d => d.dayNumber === day.dayNumber - 1));
    if (source) setEditActivity(source);
  }

  // True when this stop is the one the next day's origin card copies: the server
  // rebuilt that card on save, so the local copy of the itinerary is now stale.
  function mirroredByNextDay(activity: Activity): boolean {
    if (activity.isOrigin) return false;
    const day = days.find(d => d.id === activity.dayId);
    if (!day || lastStopOf(day)?.id !== activity.id) return false;
    return !!days.find(d => d.dayNumber === day.dayNumber + 1)?.startFromPrevStop;
  }

  async function handleActivityUpdated(activity: Activity) {
    // A cross-day move re-slots both days on the server (sortOrder, the
    // "continues from" mirrors, and — for a booking card — a brand new row
    // regenerated from the booking), so refetch instead of mirroring all of it.
    // Same for the day's last stop when the next day continues from it: the
    // server redrew that day's origin card from the edited stop.
    if ((editActivity && editActivity.dayId !== activity.dayId) || mirroredByNextDay(activity)) {
      setEditActivity(null);
      setDays(await itineraryApi.listDays(trip.id));  // selectedDay re-syncs from `days`
      return;
    }
    setDays(d => d.map(day =>
      day.id === activity.dayId
        ? { ...day, activities: day.activities.map(a => a.id === activity.id ? activity : a) }
        : day,
    ));
    if (selectedDay?.id === activity.dayId) {
      setSelectedDay(s => s ? { ...s, activities: s.activities.map(a => a.id === activity.id ? activity : a) } : s);
    }
    setEditActivity(null);
  }

  function deleteActivity(activity: Activity) {
    void deleteActivities([activity.id]);
  }

  if (loading) return <LoadingScreen />;

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
            {trip.destination.toUpperCase()} · {days.length} {t('itinerary.dayUnit', { count: days.length })}
          </p>
          <h2
            className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100"
            style={{  }}
          >
            {t('itinerary.heading')}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <UndoRedoControls history={history} />
          {/* View toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                viewMode === 'list' ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              <Icon as={List} size={14} />
              {t('itinerary.views.list')}
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                viewMode === 'timeline' ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              <Icon as={AlignLeft} size={14} />
              {t('itinerary.views.timeline')}
            </button>
          </div>
        </div>
      </div>
      </div>
      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">

      {days.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title={t('itinerary.empty.noDays')}
          body={t('itinerary.empty.noDaysDesc')}
        />
      ) : viewMode === 'timeline' ? (
        <TimelineView
          days={days}
          trip={trip}
          weather={weather}
          visitedOf={a => venueForActivity(a)?.visited}
          onToggleVisited={toggleVisited}
          onOpenHourly={setHourlyDate}
          onReorderActivities={reorderDay}
          onMoveToDay={(ids, dayId, atIndex) => crossDay.moveToDay(ids, dayId, atIndex)}
          onEditActivity={editActivityOrSource}
          onDeleteActivity={deleteActivity}
        />
      ) : (
        /* List view — DayStrip + TimelineRail */
        <div className="space-y-4">
          {/* Day strip */}
          <DayStrip
            days={days}
            activeId={selectedDay?.id ?? null}
            onSelect={setSelectedDay}
            weather={weather}
            weatherLoading={weatherLoading}
            legs={trip.destinations}
          />

          {/* Timeline for selected day */}
          {selectedDay ? (
            <div className="card p-5 animate-slide-up space-y-3">
              {/* The strip's compact chips live inside a <button>, so they
                  cannot open anything themselves. The selected day's own chip
                  carries the entry point instead. The row is rendered whether
                  or not the weather is here yet — that is what keeps the plan
                  from being shoved down when it lands. */}
              <div className="flex items-center justify-between gap-3 min-h-[24px] pb-2 border-b border-slate-100 dark:border-slate-800">
                <span className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap min-w-0">
                  <span>
                    {t('itinerary.dayLabel', { n: selectedDay.dayNumber })}
                    {' · '}
                    {format(parseISO(selectedDay.date.slice(0, 10)), 'EEE, d MMM', { locale: dateLocale })}
                    {selectedDay.title ? ` · ${selectedDay.title}` : ''}
                    {placeOf(selectedDay.dayNumber) ? ` · ${placeOf(selectedDay.dayNumber)}` : ''}
                    {' · '}
                  </span>
                  {/* The day's start hour belongs on the day's line: it is a
                      fact about the day, not a control that deserves a row. */}
                  <DayStartTime
                    value={selectedDay.startTime}
                    missingLegs={legs.missingCount(selectedDay.id)}
                    onChange={async startTime => {
                      const updated = await itineraryApi.updateDay(trip.id, selectedDay.id, { startTime });
                      setDays(d => d.map(dy => dy.id === selectedDay.id ? { ...dy, ...updated } : dy));
                    }}
                  />
                </span>
                {weather[selectedDay.date.slice(0, 10)] ? (
                  <DayWeatherChip
                    weather={weather[selectedDay.date.slice(0, 10)]}
                    variant="full"
                    locale={dateLocale}
                    onClick={() => setHourlyDate(selectedDay.date.slice(0, 10))}
                  />
                ) : weatherLoading && (
                  <span aria-hidden className="block h-[22px] w-24 shrink-0 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
                )}
              </div>

              {/* Day actions. Since ADR-0011 the open day resolves itself, so
                  this button is a retry and nothing else: it shows only while a
                  day has legs missing AND has not been through a resolve. Past
                  that point a missing leg is a leg with no route — Nusa Penida
                  to mainland Bali has no road — and offering to buy it again
                  spends nothing and finds nothing. */}
              <div className="flex items-center gap-2 flex-wrap">
                {legs.missingCount(selectedDay.id) > 0 && !legs.attempted(selectedDay.id) && (
                  <button
                    type="button"
                    disabled={legs.resolving !== null}
                    onClick={() => legs.resolveDay(selectedDay.id)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-2xs font-semibold text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                  >
                    {legs.resolving === selectedDay.id
                      ? <Spinner size="xs" branded />
                      : <Icon as={Clock} size={11} />}
                    {t('itinerary.legs.calculate', { count: legs.missingCount(selectedDay.id) })}
                  </button>
                )}
                {selectedDay.activities.filter(a => !a.isOrigin).length >= 2 && (
                  <button
                    type="button"
                    disabled={optimizer.optimizingDayId !== null}
                    onClick={() => optimizer.optimize(selectedDay.id, selectedDay.activities)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-violet-200 dark:border-violet-800 text-2xs font-semibold text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                  >
                    {optimizer.optimizingDayId === selectedDay.id
                      ? <Spinner size="xs" branded />
                      : <Icon as={Sparkles} size={11} />}
                    {t('map.optimize')}
                  </button>
                )}
              </div>
              <TimelineRail
                visitedOf={a => venueForActivity(a)?.visited}
                onToggleVisited={toggleVisited}
                day={selectedDay}
                days={days}
                onMoveToDay={(ids, dayId) => crossDay.moveToDay(ids, dayId)}
                onDraggingChange={setDragging}
                secondsAfter={legs.secondsAfter}
                transferAfter={legs.transferAfter}
                onAcceptSuggestedTime={async (activity, startTime) => {
                  // The one write this feature does, and the user asked for it
                  // by clicking. Setting the hour re-slots the day on the server
                  // (resortDayByStartTime), so the response is what gets applied.
                  try {
                    const updated = await itineraryApi.updateActivity(
                      trip.id, activity.dayId, activity.id, { startTime },
                    );
                    await handleActivityUpdated(updated);
                  } catch {
                    toast(t('itinerary.toast.failed'), 'error');
                  }
                }}
                onPlanWithAi={() => askAi(
                  t('itinerary.empty.planPrompt', { n: selectedDay.dayNumber }),
                  { send: true },
                )}
                onBrowseSaved={() => navigate(`/trips/${trip.id}/venues`)}
                getMapsUrl={resolveActivityMapsUrl}
                photosOf={stopPhotos.photosOf}
                onOpenPhoto={(photos, index) => setOpenPhotos({ photos, index })}
                prevDayLastActivity={selectedDay.dayNumber > 1
                  ? lastStopOf(days.find(d => d.dayNumber === selectedDay.dayNumber - 1))
                  : null}
                lastActivityLocked={
                  !!(days.find(d => d.dayNumber === selectedDay.dayNumber + 1)?.startFromPrevStop)
                }
                selectedIds={selectedIds}
                onToggleSelect={toggleSelectId}
                onAddActivity={setAddingTo}
                onEditActivity={editActivityOrSource}
                onDeleteActivity={deleteActivity}
                onDragEnd={reorderDay}
                startFromPrevPending={togglingStartFromPrev}
                onToggleStartFromPrev={async (enabled) => {
                  setTogglingStartFromPrev(true);
                  try {
                    const updated = await itineraryApi.updateDay(trip.id, selectedDay.id, { startFromPrevStop: enabled });
                    setDays(d => d.map(dy => dy.id === selectedDay.id ? { ...dy, ...updated } : dy));
                    setSelectedDay(s => s && s.id === selectedDay.id ? { ...s, ...updated } : s);
                  } catch {
                    toast(t('itinerary.toast.failed'), 'error');
                  } finally {
                    setTogglingStartFromPrev(false);
                  }
                }}
              />

            </div>
          ) : (
            <div className="card p-8 flex items-center justify-center">
              <p className="text-sm text-slate-400">{t('itinerary.empty.clickDay')}</p>
            </div>
          )}
        </div>
      )}

      <HourlyWeatherPanel
        open={hourlyDate !== null}
        onClose={() => setHourlyDate(null)}
        tripId={trip.id}
        placesKey={weatherKey}
        days={days}
        weather={weather}
        focusDate={hourlyDate}
      />

      {/* Modals */}
      <Modal open={!!addingTo} onClose={() => setAddingTo(null)} title={t('itinerary.modal.addActivity')}>
        {addingTo && (
          <ActivityForm
            tripId={trip.id}
            dayId={addingTo.dayId}
            venues={venues}
            days={days}
            onSaved={handleActivityCreated}
            onCancel={() => setAddingTo(null)}
          />
        )}
      </Modal>
      <Modal open={!!editActivity} onClose={() => setEditActivity(null)} title={t('itinerary.modal.editActivity')}>
        {editActivity && (
          <ActivityForm
            tripId={trip.id}
            dayId={editActivity.dayId}
            existing={editActivity}
            days={days}
            onSaved={handleActivityUpdated}
            onCancel={() => setEditActivity(null)}
            onDelete={() => { deleteActivity(editActivity); setEditActivity(null); }}
          />
        )}
      </Modal>
      </div>

    {openPhotos && (
      <StopPhotoViewer
        tripId={trip.id}
        days={days}
        canEdit={tripPermissions(trip).canEdit}
        photos={openPhotos.photos}
        index={openPhotos.index}
        onClose={() => setOpenPhotos(null)}
        onReplaced={stopPhotos.replace}
        onRemoved={stopPhotos.remove}
      />
    )}
    <LinkedDeleteModal
      open={linkedDelete !== null}
      title={t('itinerary.deleteLinked.title', { count: linkedDelete?.meals ?? 1 })}
      body={t('itinerary.deleteLinked.body', { count: linkedDelete?.meals ?? 1 })}
      onlyLabel={t('itinerary.deleteLinked.only', { count: linkedDelete?.ids.length ?? 1 })}
      onOnly={() => { const ids = linkedDelete?.ids ?? []; setLinkedDelete(null); void deleteActivities(ids, false); }}
      onBoth={() => { const ids = linkedDelete?.ids ?? []; setLinkedDelete(null); void deleteActivities(ids, true); }}
      onCancel={() => setLinkedDelete(null)}
    />

    {/* Bulk delete loading overlay */}
    <AiRouteProposalModal
      proposal={optimizer.proposal}
      currentOrder={days.find(d => d.id === optimizer.proposal?.dayId)?.activities ?? []}
      onAccept={optimizer.accept}
      onDiscard={optimizer.discard}
    />

    {/* Bulk selection action bar */}
    {selectedIds.size > 0 && !dragging && (
      <div className="fixed bottom-36 sm:bottom-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 dark:bg-slate-800 text-white rounded-2xl px-5 py-3 shadow-2xl whitespace-nowrap animate-fade-in">
        <span className="text-sm font-medium">{t('itinerary.bulk.selected', { count: selectedIds.size })}</span>
        <div className="w-px h-4 bg-white/20" />
        <div className="relative">
          <button
            type="button"
            disabled={crossDay.pending}
            onClick={() => setBulkMenu(m => (m === 'move' ? null : 'move'))}
            className="text-sm font-semibold transition-colors cursor-pointer hover:text-teal-300 disabled:opacity-40"
          >
            {t('itinerary.bulk.move')}
          </button>
          {bulkMenu === 'move' && (
            <DayPickerMenu
              days={days}
              disabledDayId={soleSelectedDayId}
              title={t('itinerary.bulk.moveTo')}
              onClose={() => setBulkMenu(null)}
              onPick={async dayId => {
                setBulkMenu(null);
                const ids = [...selectedIds];
                setSelectedIds(new Set());
                await crossDay.moveToDay(ids, dayId);
              }}
            />
          )}
        </div>
        <div className="w-px h-4 bg-white/20" />
        <div className="relative">
          <button
            type="button"
            disabled={crossDay.pending}
            onClick={() => setBulkMenu(m => (m === 'duplicate' ? null : 'duplicate'))}
            className="text-sm font-semibold transition-colors cursor-pointer hover:text-teal-300 disabled:opacity-40"
          >
            {t('itinerary.bulk.duplicate')}
          </button>
          {bulkMenu === 'duplicate' && (
            <DayPickerMenu
              days={days}
              title={t('itinerary.bulk.duplicateTo')}
              onClose={() => setBulkMenu(null)}
              onPick={async dayId => {
                setBulkMenu(null);
                const ids = [...selectedIds];
                setSelectedIds(new Set());
                await crossDay.duplicateToDay(ids, dayId);
              }}
            />
          )}
        </div>
        <div className="w-px h-4 bg-white/20" />
        <button
          type="button"
          onClick={bulkDeleteActivities}
          className="text-red-400 hover:text-red-300 text-sm font-semibold transition-colors cursor-pointer"
        >
          {t('itinerary.bulk.delete')}
        </button>
        <div className="w-px h-4 bg-white/20" />
        <button
          type="button"
          onClick={() => setSelectedIds(new Set())}
          className="text-slate-400 hover:text-white text-sm transition-colors cursor-pointer"
        >
          {t('itinerary.bulk.cancel')}
        </button>
      </div>
    )}
    </div>
  );
}
